import Head from "next/head";
import { useRouter } from "next/router";
import { SITE_URL } from "@api/constants/resources";
import { generateBreadcrumbItems } from "@/utils/breadcrumb";
import { getPageType } from "@/utils/breadcrumb/pageTypes";
import { defaultSeoConfig } from "@/utils/seo/SeoDefault";

const Seo = ({
  title,
  desc,
  image,
  keywords,
  breadcrumbData,
  id,
}) => {
  const router = useRouter();

  const pageTitle = title
    ? `${title} | 칼렛스토어`
    : defaultSeoConfig.title;

  const finalDescription = desc
    ? desc
    : defaultSeoConfig.description;

  const finalImage = image
    ? image
    : defaultSeoConfig.image;

  const finalKeywords = keywords
    ? keywords
    : defaultSeoConfig.keywords;

  /*
   * URL 정리
   *
   * 예:
   * /products/Step-For-Net-Zero/2/?abc=1
   *
   * ↓
   *
   * /products/Step-For-Net-Zero/2/
   */
  const cleanPath = router.asPath
    .split("?")[0]
    .split("#")[0];

  /*
   * pathSegments
   *
   * /products/Step-For-Net-Zero/2/
   *
   * ↓
   *
   * [
   *   "products",
   *   "Step-For-Net-Zero",
   *   "2"
   * ]
   */
  const pathSegments = cleanPath
    .split("/")
    .filter(Boolean);

  // 페이지 타입 판별
  const pageType = getPageType(pathSegments);

  /*
   * Canonical URL
   *
   * 페이지네이션 번호를 제거하지 않습니다.
   *
   * /products/Step-For-Net-Zero/
   * →
   * https://www.caretstore.co.kr/products/Step-For-Net-Zero/
   *
   * /products/Step-For-Net-Zero/2/
   * →
   * https://www.caretstore.co.kr/products/Step-For-Net-Zero/2/
   */
  const normalizedPath =
    cleanPath === "/"
      ? "/"
      : cleanPath.endsWith("/")
      ? cleanPath
      : `${cleanPath}/`;

  const canonicalUrl = `${SITE_URL}${normalizedPath}`;

  /*
   * 상품 상세 URL인지 확인
   *
   * 예:
   * /products/Cafe-Supplies/P2501091606403035/
   *
   * 상품 상세 페이지의 canonical은
   * ProductsDetail.js에서 별도로 처리하므로
   * 여기에서는 canonical 출력하지 않음
   */
  const hasPIdPattern = pathSegments.some((segment) =>
    /^P\d{10,}$/.test(segment)
  );

  /*
   * Breadcrumb Schema 생성
   */
  const generateBreadcrumbSchema = () => {
    const breadcrumbItems = generateBreadcrumbItems(
      pageType,
      breadcrumbData || {},
      pathSegments,
      canonicalUrl,
      id
    );

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbItems,
    };
  };

  return (
    <Head>
      {/* 기본 SEO */}
      <title>{pageTitle}</title>

      <meta
        name="description"
        content={finalDescription}
      />

      <meta
        name="author"
        content={defaultSeoConfig.author}
      />

      <meta
        name="keywords"
        content={finalKeywords}
      />

      <meta
        name="image"
        content={finalImage}
      />

      {/*
        robots index, follow는 기본 동작이므로
        여기에서는 별도로 선언하지 않습니다.

        판매중지 상품 등의 noindex 처리는
        해당 페이지에서 직접 설정합니다.
      */}

      {/*
        Canonical URL

        상품 상세 페이지는 ProductsDetail.js에서
        canonical을 직접 생성하므로 제외합니다.
      */}
      {!hasPIdPattern && (
        <link
          rel="canonical"
          href={canonicalUrl}
        />
      )}

      {/* Naver 사이트 인증 */}
      <meta
        name="naver-site-verification"
        content="73434c46afaf95b1aca89b69bf1bbd9b18d465c8"
      />

      {/* Open Graph */}
      <meta
        property="og:site_name"
        content={defaultSeoConfig.siteName}
      />

      <meta
        property="og:title"
        content={pageTitle}
      />

      <meta
        property="og:description"
        content={finalDescription}
      />

      <meta
        property="og:url"
        content={canonicalUrl}
      />

      <meta
        property="og:type"
        content={pageType === "product" && hasPIdPattern ? "product" : "website"}
      />

      <meta
        property="og:image"
        content={finalImage}
      />

      {/* Twitter Card */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={pageTitle}
      />

      <meta
        name="twitter:description"
        content={finalDescription}
      />

      <meta
        name="twitter:image"
        content={finalImage}
      />

      {/* Favicon */}
      <link
        rel="icon"
        href="/favicon.svg"
      />

      <link
        rel="manifest"
        href="/manifest.json"
      />

      <meta
        name="theme-color"
        content="#3bb77e"
      />

      {/* Google 사이트 인증 */}
      <meta
        name="google-site-verification"
        content="Rra2K_-5diyQmqHmSncqz4EJUQ3aXQLOgJ95bsVqrDY"
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateBreadcrumbSchema()
          ),
        }}
      />
    </Head>
  );
};

export default Seo;